/**
 * Created by ADVENT\mzhao on 03/10/2022.
 * Description:
 * ExchangeSyncContact Component
 * ------ maintenance history ------
 */
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { ExchangeService } from '../../../tamalelibs/services/exchange.service';
import { of as observableOf } from 'rxjs';
import { SlideSheetService } from '../../../services/slide-sheet.service';
import { SlideSheetActionTypes } from '../../slide-sheet/slide-sheet.model';
import { NotificationStyles, NotificationOptions } from '../../../widgets/notification/notification.model';
import { ToastService } from '../../../widgets/toast/toast.service';
import { StringLiteralsPipe } from '../../../pipes/translate.pipe';
import { businessConstants } from '../../../constants/business.constants';

@Component({
    selector: 'tam-exchange-sync-contact',
    templateUrl: './exchange-sync-contact.component.html',
    styleUrls: ['./exchange-sync-contact.component.scss']
})
export class ExchangeSyncContactComponent implements OnInit, OnDestroy {
    //#region public properties
    clickLabelStatus: boolean;
    currentSyncRule: string;
    domainData: Array<String> = new Array<String>();
    isFromAdminSetting: boolean;
    isShowDomianError = false;
    isSyncDisabled: boolean;
    isShowCheckBoxField: boolean;
    selectedDomainItems: Array<String> = [];
    syncToExchange: boolean;
    syncToTamale: boolean;
    syncByUpdate: boolean;
    syncRule: string;
    syncRules: Array<string> = [
        StringLiteralsPipe.translate('exchange.contact_from_tamale'),
        StringLiteralsPipe.translate('exchange.contact_from_exchange'),
        StringLiteralsPipe.translate('exchange.last_modified_date')
    ];
    //#endregion

    //#region private properties

    /**
     * the subscription array that would be unsubscribed on destroying.
     */
    private _destroySubscriptions: Array<Subscription> = [];

    //#endregion

    constructor(
        private _slideSheetService: SlideSheetService,
        private _exchangeService: ExchangeService,
        private _toast: ToastService) { }

    //#region public methods

    ngOnInit() {
        this._initDataForShowSyncConfig();
    }

    ngOnDestroy(): void {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
        this._destroySubscriptions = [];
    }

    /**
    * Apply the configuration for firm or individual.
    *
    * @returns
    * @memberof ExchangeSyncContactComponent
    */
    clickApply() {
        this.isSyncDisabled = true;
        const _successToast: NotificationOptions = {
            message: StringLiteralsPipe.translate('exchange.auto_synchronization_settings_applied_successfully'),
            style: NotificationStyles.Success

        };
        if (this.selectedDomainItems && this.selectedDomainItems.length === 0 && this.isFromAdminSetting) {
            this.isShowDomianError = true;
            this.isSyncDisabled = false;
            return;
        }
        if (this.isFromAdminSetting) {
            this._exchangeService.putConfigSynchronization(this._mappingExchangeConfig(), this.syncRule)
                .pipe(
                    take(1),
                    catchError((error) => {
                        if (error) {
                            this._setError(error);
                        }
                        return observableOf(null);
                    }),

                ).subscribe(res => {
                    if (res) {
                        this._toast.notify(_successToast);
                    }
                    this.isSyncDisabled = false;
                    this.clickClose();
                });
        } else {
            this._exchangeService.putIndividualSubscriptionConfig(this._mappingExchangeConfig())
                .pipe(
                    take(1),
                    catchError((error) => {
                        this._setError(error);
                        return observableOf(null);
                    }),
                ).subscribe(res => {
                    if (res) {
                        this._toast.notify(_successToast);
                    }
                    this.isSyncDisabled = false;
                    this.clickClose();
                });
        }
    }

    clickClose() {
        this._slideSheetService.slideSheetActionSubject$.next({
            type: SlideSheetActionTypes.CLOSE,
            payload: {
                slideSheetAutoClose: false,
                autoClose: false
            }
        });
    }

    valueChangeDomain() {
        if (this.selectedDomainItems && this.selectedDomainItems.length > 0) {
            this.isShowDomianError = false;
        } else {
            this.isShowDomianError = true;
        }
    }

    //#endregion

    //#region private methods

    /**
     * get sync config and domain list.
     *
     * @private
     * @memberof ExchangeSyncContactComponent
     */
    private _initDataForShowSyncConfig() {
        this.isShowCheckBoxField = false;
        if (this.isFromAdminSetting) {
            this._destroySubscriptions.push(this._exchangeService.getFirmSubscriptionDomain().pipe(
                take(1),
                catchError(() => {
                    return observableOf(null);
                }),
            ).subscribe(res => {
                if (res) {
                    this.domainData = res;
                }
            }),
                this._exchangeService.getFirmSubscriptionConfig().pipe(
                    take(1),
                    catchError((error) => {
                        this._setError(error);
                        return observableOf(null);
                    }),
                ).subscribe(res => {
                    this.isShowCheckBoxField = true;
                    if (res.firmSubscriptionConfig && res.firmSubscriptionConfig.configs) {
                        res.firmSubscriptionConfig.configs.forEach(item => {
                            if (item.resourceType === businessConstants.Contacts.contacts) {
                                this.syncToTamale = item.eChangeType.created;
                                this.syncToExchange = item.tChangeType.created;
                                this.syncByUpdate = item.tChangeType.updated;
                            }
                        });
                    }
                    // set current sync rule
                    this.currentSyncRule = res.syncRule;
                    this.syncRule = this.syncRules.find(item => item === res.syncRule);
                    this.selectedDomainItems = res.firmSubscriptionConfig.domains;
                }));
        } else {
            this._destroySubscriptions.push(
                this._exchangeService.getIndividualSubscriptionConfig().pipe(
                    take(1),
                    catchError((error) => {
                        this._setError(error);
                        return observableOf(null);
                    }),
                ).subscribe(res => {
                    this.isShowCheckBoxField = true;
                    if (res) {
                        if (res.personSubscriptionConfig) {
                            res.personSubscriptionConfig.forEach(item => {
                                if (item.resourceType === businessConstants.Contacts.contacts) {
                                    this.syncToTamale = item.eChangeType.created;
                                    this.syncToExchange = item.tChangeType.created;
                                    this.syncByUpdate = item.tChangeType.updated;
                                }
                            });
                        }
                        // set current sync rule
                        this.currentSyncRule = res.syncRule;
                    }
                }));
        }
    }

    /**
     * Mapping exchange config for params
     *
     * @private
     * @returns
     * @memberof ExchangeSyncContactComponent
     */
    private _mappingExchangeConfig() {
        const param = [{
            'eChangeType': {
                'deleted': false,
                'created': this.syncToTamale,
                'updated': this.syncByUpdate
            },
            'tChangeType': {
                'deleted': false,
                'created': this.syncToExchange,
                'updated': this.syncByUpdate,
            },
            'resourceType': 'contacts'
        }];
        // only firm need domain
        if (this.isFromAdminSetting) {
            return { 'configs': param, 'domains': this.selectedDomainItems };
        } else {
            return param;
        }
    }

    private _setError(error) {
        if (error) {
            const _failedToast: NotificationOptions = {
                message: error.message,
                style: NotificationStyles.Error
            };
            this._toast.notify(_failedToast);
        }
    }

    //#endregion
}
